export interface SelectableAudioTrack {
  label: string
  highlighted: boolean
  sourceVersionLabel?: string
}

export interface TimedAudioTrack extends SelectableAudioTrack {
  startS: number
  endS: number
  sourceDurationS?: number
  sourceType?: 'tts' | 'clip'
}

export interface SourceTimelineSegment {
  start_s: number
  end_s: number
}

const TIMING_EPSILON_S = 0.01

function trackKey(track: SelectableAudioTrack): string {
  return track.sourceVersionLabel ?? track.label
}

function isOrigTrack(track: SelectableAudioTrack): boolean {
  const key = trackKey(track)
  return key === 'orig' || key === 'STT'
}

export function getSelectedTrackIndex<T extends SelectableAudioTrack>(tracks: T[]): number {
  if (tracks.length === 0) return -1

  const highlightedIndices = tracks
    .map((track, index) => ({ track, index }))
    .filter(({ track }) => track.highlighted)
    .map(({ index }) => index)

  if (highlightedIndices.length === 1) return highlightedIndices[0]

  const firstHighlightedNonOrig = highlightedIndices.find((index) => !isOrigTrack(tracks[index]))
  if (firstHighlightedNonOrig !== undefined) return firstHighlightedNonOrig

  if (highlightedIndices.length > 1) return highlightedIndices[0]

  const firstNonOrig = tracks.findIndex((track) => !isOrigTrack(track))
  if (firstNonOrig !== -1) return firstNonOrig

  return 0
}

export function normalizeTrackSelection<T extends SelectableAudioTrack>(tracks: T[]): T[] {
  const selectedIndex = getSelectedTrackIndex(tracks)
  if (selectedIndex === -1) return tracks
  return tracks.map((track, index) => ({
    ...track,
    highlighted: index === selectedIndex,
  }))
}

export function selectTrackExclusive<T extends SelectableAudioTrack>(
  tracks: T[],
  selectedIndex: number,
): T[] {
  if (selectedIndex < 0 || selectedIndex >= tracks.length) return tracks
  return tracks.map((track, index) => ({
    ...track,
    highlighted: index === selectedIndex,
  }))
}

export function trackDurationS(track: TimedAudioTrack): number {
  return Math.max(0, track.endS - track.startS)
}

export function resolveTrackSourceDurationS(
  track: TimedAudioTrack,
  sourceSegment: SourceTimelineSegment | undefined,
  selectionChanged: boolean,
): number {
  const currentDurationS = trackDurationS(track)
  if (selectionChanged) return currentDurationS
  if (track.sourceType === 'clip' || isOrigTrack(track)) {
    return track.sourceDurationS ?? currentDurationS
  }
  if (!sourceSegment) {
    return track.sourceDurationS ?? currentDurationS
  }

  const sourceWindowDurationS = Math.max(0, sourceSegment.end_s - sourceSegment.start_s)
  const matchesSourceWindow = Math.abs(currentDurationS - sourceWindowDurationS) <= TIMING_EPSILON_S
  if (matchesSourceWindow) return currentDurationS

  return track.sourceDurationS ?? currentDurationS
}

export function shouldDuckOriginalForTrack(track: SelectableAudioTrack): boolean {
  return !isOrigTrack(track)
}
